import AddIcon from "@mui/icons-material/Add"
import { Box, Card, Typography } from "@mui/material"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { SquareBadge } from "../../../../../../../../../../../components/badges/SquareBadge"
import { UnderlinedButton } from "../../../../../../../../../../../components/buttons/UnderlinedButton"
import { IconTrash } from "../../../../../../../../../../../components/custom-icons/icon-trash"
import { AssignedParking } from "../../../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/AssignedParking"
import { levelNumberToLabel } from "../../../../../../../../../../../core/services/helper-service"
import { BSParkingListContext } from "../../../../BSBuildingParkingPage/context/BSParkingListContext"
import { GroupLevel } from "../../../context/GroupLevel"
import { ParkingAssignmentContext, ParkingAssignmentContextProvider } from "../../../context/ParkingAssignmentContext"
import { GroupButton } from "../../GroupButton"
import { BSBuildingLevelGroupInfos } from "./BSBuildingLevelGroupInfos"
import { MergeGroupLevelDialog } from "./MergeGroupLevelDialog"

import { BSParkingContext } from "../../../../BSBuildingParkingPage/context/BSParkingContext"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"
import { MorphologyAccordion } from "./MorphologyAccordion"
import { ParkingAssignedInfos } from "./ParkingAssignedInfos"
import { ParkingAssignmentForm } from "./ParkingAssignmentForm"
import { ZoneInfos } from "./ZoneInfos"

interface IProps {
  groupLevel: GroupLevel
  handlePatioControl(): void
}

export function GroupLevelCard({ groupLevel, handlePatioControl }: Readonly<IProps>): React.JSX.Element {
  return (
    <ParkingAssignmentContextProvider>
      <GroupLevelCardCore groupLevel={groupLevel} handlePatioControl={handlePatioControl} />
    </ParkingAssignmentContextProvider>
  )
}

function GroupLevelCardCore({ groupLevel, handlePatioControl }: Readonly<IProps>): React.JSX.Element {
  const { setSelectedGroupLevel } = useContext(BSLevelFormContext)
  const { groupLevelSuperstructureListForm, groupLevelInfrastructureListForm, splitGroupLevel, deleteGroupLevel } =
    useContext(BSLevelFormContext)
  const { superstructureParkingList } = useContext(BSParkingListContext)
  const { isEditing } = useContext(ParkingAssignmentContext)
  const { setIsParkingFormOpen, setIsParkingFormForSuperstructure } = useContext(BSParkingContext)

  const [displayAssignmentParkingForm, setDisplayAssignmentParkingForm] = useState<boolean>(false)
  const [isMergeGroupLevelDialogOpen, setIsMergeGroupLevelDialogOpen] = useState<boolean>(false)

  const hasAssignedParking = useMemo(() => groupLevel.levelList[0].bsParkingList.length > 0, [groupLevel.levelList])

  const assignedParkingList: AssignedParking[] = useMemo(
    () => groupLevel.levelList?.[0]?.bsParkingList || [],
    [groupLevel.levelList]
  )

  const parkingPercentageSum = useMemo(
    () => assignedParkingList.reduce((sum, parking) => sum + (parking.percentage ?? 0), 0),
    [assignedParkingList]
  )

  const hasAllSuperstructureParkingAssigned = useMemo(
    () => assignedParkingList.length === superstructureParkingList.length,
    [assignedParkingList.length, superstructureParkingList.length]
  )

  const isSuperstructure = useMemo(() => groupLevel.levelList[0].superstructure, [groupLevel.levelList])

  const handleDivideGroupLevel: () => void = useCallback(() => {
    splitGroupLevel(groupLevel, groupLevel.levelList[0].superstructure)
  }, [groupLevel, splitGroupLevel])

  const [groupOptionsToDisplay, setGroupOptionsToDisplay] = useState<GroupLevel[]>([])

  const handleGroupOptionsToDisplay: (isGroupSuperstructure: boolean) => void = useCallback(
    (isGroupSuperstructure) => {
      const groupList = isGroupSuperstructure ? groupLevelSuperstructureListForm : groupLevelInfrastructureListForm

      const groupIndex = groupList.findIndex((group) => group.id === groupLevel.id)

      const groupOptions: GroupLevel[] = []
      if (groupIndex > 0) {
        groupOptions.push(groupList[groupIndex - 1])
      }

      if (groupIndex < groupList.length - 1) {
        groupOptions.push(groupList[groupIndex + 1])
      }

      setGroupOptionsToDisplay(groupOptions)
    },
    [groupLevelSuperstructureListForm, groupLevelInfrastructureListForm, groupLevel.id]
  )

  const handleDisplayMergeGroupDialog: () => void = useCallback(() => {
    handleGroupOptionsToDisplay(isSuperstructure)
    setIsMergeGroupLevelDialogOpen(true)
  }, [handleGroupOptionsToDisplay, isSuperstructure])

  const handleDeteleGroupLevel: () => void = useCallback(() => {
    deleteGroupLevel(groupLevel, isSuperstructure)
    handlePatioControl()
  }, [deleteGroupLevel, groupLevel, isSuperstructure, handlePatioControl])

  const handleDisplayParkingOptions: () => void = useCallback(() => {
    setSelectedGroupLevel(groupLevel)

    if ((isSuperstructure && superstructureParkingList.length === 0) || hasAllSuperstructureParkingAssigned) {
      setIsParkingFormForSuperstructure(isSuperstructure)
      setIsParkingFormOpen(true)
    } else {
      setDisplayAssignmentParkingForm(true)
    }
  }, [
    groupLevel,
    hasAllSuperstructureParkingAssigned,
    isSuperstructure,
    setIsParkingFormForSuperstructure,
    setIsParkingFormOpen,
    setSelectedGroupLevel,
    superstructureParkingList.length,
  ])

  return (
    <Card sx={{ borderRadius: 4, p: 2, boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)", my: 2 }}>
      <Box display="flex" alignItems="center" gap={2}>
        {groupLevel.levelList.length > 1 ? (
          <Typography variant="body2" fontWeight="600">
            [{levelNumberToLabel(groupLevel?.levelList[0]?.levelNumber)}] ; [
            {levelNumberToLabel(groupLevel?.levelList[groupLevel.levelList.length - 1].levelNumber)}]
          </Typography>
        ) : (
          <Typography variant="body2" fontWeight="600">
            {levelNumberToLabel(groupLevel.levelList[0]?.levelNumber)}
          </Typography>
        )}
        <SquareBadge
          content={groupLevel.levelList.length}
          color={customPalette.lightBlue5}
          backgroundColor={customPalette.lightBlue1}
        />
        <Box
          sx={{
            flexGrow: 1,
            height: "2px",
            backgroundColor: customPalette.paperBorder,
            marginLeft: "6px",
          }}
        />
        {groupLevel.levelList.length > 1 ? (
          <GroupButton isGrouped onClick={handleDivideGroupLevel} />
        ) : (
          <GroupButton isGrouped={false} onClick={handleDisplayMergeGroupDialog} />
        )}
        <SquareBadge
          onClick={handleDeteleGroupLevel}
          icon={<IconTrash sx={{ color: customPalette.lightBlue2 }} />}
          backgroundColor={customPalette.lightGray7}
          cursor="pointer"
        />
      </Box>

      {isMergeGroupLevelDialogOpen && (
        <MergeGroupLevelDialog
          groupLevel={groupLevel}
          isSuperstructure={isSuperstructure}
          groupOptionsToDisplay={groupOptionsToDisplay}
          setGroupOptionsToDisplay={setGroupOptionsToDisplay}
          isOpen={isMergeGroupLevelDialogOpen}
          setIsOpen={setIsMergeGroupLevelDialogOpen}
        />
      )}

      <BSBuildingLevelGroupInfos groupLevel={groupLevel} />

      <MorphologyAccordion groupLevel={groupLevel} />

      <Box m={1}>
        <Typography variant="body2" fontWeight={600} mb={1}>
          {isSuperstructure ? "Zones & Parkings" : "Parkings"}
        </Typography>

        {isSuperstructure && <ZoneInfos groupLevel={groupLevel} parkingPercentageSum={parkingPercentageSum} />}

        {hasAssignedParking &&
          assignedParkingList.map((parking, index) => (
            <ParkingAssignedInfos
              key={parking.parkingId}
              parkingIndex={index}
              groupLevel={groupLevel}
              assignedParking={parking}
              displayAssignmentParkingForm={displayAssignmentParkingForm}
              hasAllSuperstructureParkingAssigned={hasAllSuperstructureParkingAssigned}
              parkingPercentageSum={parkingPercentageSum}
            />
          ))}

        {displayAssignmentParkingForm && (
          <Box flex={11}>
            <ParkingAssignmentForm
              groupLevel={groupLevel}
              setDisplayParkingOptions={setDisplayAssignmentParkingForm}
              parkingPercentageSum={parkingPercentageSum}
            />
          </Box>
        )}
      </Box>

      {!displayAssignmentParkingForm && !isEditing && isSuperstructure && (
        <UnderlinedButton
          icon={AddIcon}
          label={isSuperstructure ? "Affecter un parking en superstructure" : "Affecter un parking en infrastructure"}
          onClick={handleDisplayParkingOptions}
        />
      )}
    </Card>
  )
}
